import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Descriptions, Empty, List, Modal, Progress, Select, Space, Spin, Tag, Typography, message } from 'antd';
import { SwapOutlined, PlusOutlined, RocketOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { userPath } from '../config/paths';
import { formatDate } from '../utils/date';
import { useTranslation } from '../i18n';
import RenewAction from './RenewAction';
import SubscriptionNameText from './SubscriptionNameText';
import AddonPanel from './AddonPanel';

const { Text } = Typography;

const fmtUsage = (key, v) => {
    const n = Number(v) || 0;
    if (key.includes('bytes')) {
        if (n >= 1024 ** 4) return `${(n / 1024 ** 4).toFixed(2)} TB`;
        if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
        return `${(n / 1024 ** 2).toFixed(1)} MB`;
    }
    return n.toLocaleString();
};

const Bar = ({ label, used, limit, unlimited, fmt }) => {
    const { t } = useTranslation();
    const inf = unlimited || limit < 0;
    const pct = inf ? 0 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Text type="secondary" style={{ minWidth: 90 }}>{t(label)}</Text>
            <Progress percent={pct} size="small" style={{ flex: 1, minWidth: 160, margin: 0 }}
                status={!inf && used > limit ? 'exception' : undefined}
                format={() => `${fmt ? fmt(used) : used} / ${inf ? t('Unlimited') : (fmt ? fmt(limit) : limit)}`} />
        </div>
    );
};

/**
 * 域名/区域自己的套餐面板：套餐、到期续费、配额与用量、已绑定资源、转移套餐、资源包。
 * type = 'cdn' | 'dns'。可绑定多个资源的套餐，配额与绑定列表只在主资源上展示；其他资源显示所属主资源。
 */
const ResourcePlanCard = ({ type, resourceId, onChanged }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const isCdn = type === 'cdn';
    const noun = isCdn ? t('Domains') : t('Zone');
    const plansPath = type === 'cdn' ? `/cdn/plans?domain_id=${resourceId}` : `/dns/plans?zone_id=${resourceId}`;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await apiClient.get('/api/user/plan/resource', { params: { type, id: resourceId } });
            setInfo(r.data?.success ? r.data.data : null);
        } catch (_) {
            setInfo(null);
        } finally {
            setLoading(false);
        }
    }, [type, resourceId]);
    useEffect(() => { if (resourceId) load(); }, [load, resourceId]);

    const done = () => { load(); onChanged && onChanged(); };

    const post = async (url, body, okMsg) => {
        try {
            const r = await apiClient.post(url, { type, ...body });
            if (r.data?.success) { message.success(okMsg); done(); return true; }
            message.error(r.data?.error || t('Operation failed'));
        } catch (e) {
            message.error(e?.response?.data?.error || t('Operation failed'));
        }
        return false;
    };

    // 选一个目标资源的确认框（转移 / 绑定）
    const pickTarget = ({ title, content, okText, onPick }) => {
        let picked = null;
        Modal.confirm({
            title,
            width: 480,
            content: (
                <Space direction="vertical" style={{ width: '100%' }}>
                    {content && <Text type="secondary">{content}</Text>}
                    <Select
                        style={{ width: '100%' }}
                        placeholder={t('Select {{noun}}', { noun })}
                        showSearch
                        optionFilterProp="label"
                        options={(info?.candidates || []).map((c) => ({ value: c.id, label: c.label }))}
                        onChange={(v) => { picked = v; }}
                    />
                </Space>
            ),
            okText,
            cancelText: t('Cancel'),
            onOk: async () => {
                if (!picked) { message.warning(t('Please select a {{noun}}', { noun })); throw new Error('no target'); }
                const ok = await onPick(picked);
                if (!ok) throw new Error('failed');
            },
        });
    };

    if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
    if (!info) {
        return (
            <Empty description={t('This {{noun}} has no plan yet', { noun })}>
                <Button type="primary" onClick={() => navigate(userPath(plansPath))}>{t('Select a plan')}</Button>
            </Empty>
        );
    }

    const s = info.subscription;
    const isMemberOnly = info.shared && !info.is_primary;

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card size="small">
                <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                    <Descriptions.Item label={t('Plan')}>
                        <Space wrap>
                            <Tag color={s.is_free ? 'default' : 'blue'} style={{ margin: 0 }}>{s.product_name}</Tag>
                                                    </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('Name')}><SubscriptionNameText id={s.id} name={s.name} strong={false} /></Descriptions.Item>
                    <Descriptions.Item label={t('Expires')}>
                        {s.is_free ? <Text type="secondary">{t('No expiry')}</Text> : <RenewAction billing={info.billing} resourceName={(info.members || []).find((m) => m.id === Number(resourceId))?.label} />}
                    </Descriptions.Item>
                    {info.shared && (
                        <Descriptions.Item label={t('{{noun}}s', { noun })}>
                            {info.members.length} / {info.limit < 0 ? t('Unlimited') : info.limit}
                        </Descriptions.Item>
                    )}
                </Descriptions>
                <Space wrap style={{ marginTop: 8 }}>
                    {!isMemberOnly && (
                        <Button size="small" type="primary" icon={<RocketOutlined />} onClick={() => navigate(userPath(plansPath))}>
                            {s.is_free ? t('Upgrade to a paid plan') : t('Upgrade / downgrade')}
                        </Button>
                    )}
                    {!s.is_free && !isMemberOnly && (
                        <Button size="small" icon={<SwapOutlined />} disabled={!info.candidates.length}
                            onClick={() => pickTarget({
                                title: t('Transfer plan to another {{noun}}', { noun }),
                                content: t('The plan and its expiry move over; this {{noun}} drops to the free plan. The target must be on free or no plan.', { noun }),
                                okText: t('Transfer'),
                                onPick: (to) => post('/api/user/plan/transfer', { from_id: resourceId, to_id: to }, t('Transferred')),
                            })}>
                            {t('Transfer plan')}
                        </Button>
                    )}
                </Space>
            </Card>

            {isMemberOnly && (
                <Alert type="info" showIcon
                    message={t(isCdn ? 'This domain is on the plan of "{{primary}}"; see usage and quotas on "{{primary}}"' : 'This zone is on the plan of "{{primary}}"; see usage and quotas on "{{primary}}"', { primary: info.primary_label || `#${info.primary_id}` })}
                    action={<Button size="small" danger onClick={() => Modal.confirm({
                        title: t('Unbind from this plan?'),
                        content: t(isCdn ? 'After unbinding, this domain returns to the free plan.' : 'After unbinding, this zone returns to the free plan.'),
                        onOk: () => post('/api/user/plan/detach', { id: resourceId }, t('Unbound')),
                    })}>{t('Unbind')}</Button>} />
            )}

            {!isMemberOnly && (info.quotas.length > 0 || info.usage.length > 0) && (
                <Card size="small" title={info.shared ? t(isCdn ? 'Usage (all bound domains)' : 'Usage (all bound zones)') : t('Usage')}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {info.usage.map((u) => (
                            <Bar key={u.feature_key} label={u.label} used={u.used} limit={u.limit} unlimited={u.unlimited} fmt={(v) => fmtUsage(u.feature_key, v)} />
                        ))}
                        {info.quotas.map((q) => <Bar key={q.key} label={q.label} used={q.used} limit={q.limit} />)}
                    </Space>
                </Card>
            )}

            {info.shared && info.is_primary && (
                <Card size="small" title={t(isCdn ? 'Bound domains' : 'Bound zones')}
                    extra={<Button size="small" icon={<PlusOutlined />}
                        disabled={!info.candidates.length || (info.limit > 0 && info.members.length >= info.limit)}
                        onClick={() => pickTarget({
                            title: t(isCdn ? 'Bind a domain to this plan' : 'Bind a zone to this plan'),
                            okText: t('Bind'),
                            onPick: (to) => post('/api/user/plan/attach', { primary_id: resourceId, to_id: to }, t('Bound')),
                        })}>{t('Add {{noun}}', { noun })}</Button>}>
                    <List
                        size="small"
                        dataSource={info.members}
                        renderItem={(m) => (
                            <List.Item actions={m.id === info.primary_id ? [<Tag key="p" color="gold">{t('Primary')}</Tag>] : [
                                <Button key="d" size="small" type="link" danger onClick={() => Modal.confirm({
                                    title: t('Unbind {{name}} from this plan?', { name: m.label }),
                                    content: t('It drops back to the free plan.', { noun }),
                                    onOk: () => post('/api/user/plan/detach', { id: m.id }, t('Unbound')),
                                })}>{t('Unbind')}</Button>,
                            ]}>
                                {m.label}
                            </List.Item>
                        )}
                    />
                </Card>
            )}

            {!isMemberOnly && !s.is_free && <AddonPanel subscriptionId={s.id} onChanged={load} />}
            {s.expiry_date && !s.is_free && <Text type="secondary" style={{ fontSize: 12 }}>{t('Expires {{date}}', { date: formatDate(s.expiry_date) })}</Text>}
        </Space>
    );
};

export default ResourcePlanCard;
